import {
  getCountryFromLocale,
  getLanguageFromLocale,
  getOptionalLocaleFromPathname,
  isValidLocale,
  type Locale,
} from '@susu/locale-utils'
import { getSegmentCommonProperties } from '@susu/segment-common'

import { errorHandler } from './utils'

/**
 * What only the application that owns the page can know about it, when the
 * page itself does not say: the locale of a page whose path has none (the
 * Custom Made configurator on `/configurator`), and the customer of an
 * in-store page, where a staff advisor works in the customer's profile and no
 * customer cookie exists.
 *
 * Page-level on purpose. These properties are the same on every event of the
 * page, whichever application sends it (storefront and measurements on one
 * PDP must agree), so they are declared once by the page owner and never by a
 * composed application.
 */
export type PageContext = {
  /** The page's locale, e.g. `en-nl`. Used only when the path has none. */
  locale?: string
  /** The customer the page is about. Sends `user_id` and `logged_in: 'true'`. */
  customerId?: string
  /** The page's type, e.g. `custommade`. Wins over the path-based default. */
  pageType?: string
}

let pageOwner: string | undefined
// Logged once per application: an embedded page owner (Custom Made inside the
// storefront) keeps calling, and one line says enough.
const rejectedUpdaters = new Set<string>()
let pageContext: { locale?: Locale; customerId?: string; pageType?: string } =
  {}

const toValidLocale = (locale: string | undefined): Locale | undefined => {
  if (!locale) {
    return undefined
  }
  const normalized = locale.toLowerCase()
  if (isValidLocale(normalized)) {
    return normalized
  }
  errorHandler(
    new Error(
      `[analytics-client] page locale '${locale}' is not a known locale, so country and language stay page-derived.`,
    ),
  )
  return undefined
}

const applyPageContext = (context: PageContext): void => {
  pageContext = {
    locale:
      'locale' in context ? toValidLocale(context.locale) : pageContext.locale,
    customerId:
      'customerId' in context
        ? context.customerId || undefined
        : pageContext.customerId,
    pageType:
      'pageType' in context
        ? context.pageType || undefined
        : pageContext.pageType,
  }
}

/**
 * Records the page owner and its context. Called by `initializeAnalytics` for
 * the first host only: a second host on the page does not own it.
 * @internal
 */
export const setPageOwner = (
  applicationId: string,
  context: PageContext = {},
): void => {
  if (pageOwner !== undefined) {
    return
  }
  pageOwner = applicationId
  applyPageContext(context)
}

/**
 * Updates the page context after initialization, for values that change
 * during the page's life (the in-store customer). Only the page owner may call
 * it: from any other application it is ignored (and logged once), so a
 * composed application cannot make one page's events disagree.
 *
 * A key that is present replaces the current value (`undefined` or `''`
 * clears it); a key that is absent keeps it.
 *
 * @param applicationId - The page owner's application id
 * @param context - The values to change
 */
export const updatePageContext = (
  applicationId: string,
  context: PageContext,
): void => {
  if (applicationId !== pageOwner) {
    if (rejectedUpdaters.has(applicationId)) {
      return
    }
    rejectedUpdaters.add(applicationId)
    errorHandler(
      new Error(
        `[analytics-client] updatePageContext('${applicationId}') ignored: only the page owner${pageOwner ? ` ('${pageOwner}')` : ''} sets the page context.`,
      ),
    )
    return
  }
  applyPageContext(context)
}

/**
 * The common properties of the current page: `@susu/segment-common`'s
 * page-derived values, completed by the page owner's context where the page
 * says nothing. Both the Segment integration plugin and the Avo system
 * properties read this, so every event of the page carries the same values.
 */
export const getPageCommonProperties = (): ReturnType<
  typeof getSegmentCommonProperties
> => {
  const common = getSegmentCommonProperties()
  const { locale, customerId, pageType } = pageContext
  const pathHasLocale =
    getOptionalLocaleFromPathname(window.location.pathname) !== undefined

  return {
    ...common,
    ...(locale && !pathHasLocale
      ? {
          country: getCountryFromLocale(locale).toLowerCase(),
          language: getLanguageFromLocale(locale),
        }
      : {}),
    ...(customerId ? { user_id: customerId, logged_in: 'true' } : {}),
    ...(pageType ? { page_type: pageType } : {}),
  }
}

/**
 * The page type the page owner declared, if any. While none is declared the
 * plugin still takes a page_type an application put on the event (storefront's
 * store_overview / store_details, booking-tool-v2, active-customers-list) until
 * those page owners declare theirs.
 */
export const getDeclaredPageType = (): string | undefined =>
  pageContext.pageType
